import axios, { type AxiosInstance } from 'axios';
import { usePortalAuthStore } from '@/stores/portalAuthStore';

/**
 * Axios client for the external client portal (epic 128).
 *
 * Separate from `lib/apiClient.ts` on purpose: it attaches the PORTAL token
 * (never the staff token) and, on 401, it does NOT attempt a refresh — portal
 * sessions have no refresh token. A dead session is cleared and the guard
 * bounces the visitor to `/portal/login`. Sharing apiClient would (a) attach a
 * staff bearer to portal reads if both sessions exist, and (b) loop on the
 * staff `/auth/refresh` path for a token that can't be refreshed.
 */
export const portalClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

portalClient.interceptors.request.use((config) => {
  const { session } = usePortalAuthStore.getState();
  if (session?.accessToken) {
    config.headers.set('Authorization', `Bearer ${session.accessToken}`);
  }
  return config;
});

portalClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // The login call surfaces its own 401 (bad credentials) to the form; only
    // an authenticated read returning 401 means the session died.
    const url = error?.config?.url ?? '';
    const isLogin = url.includes('/portal/login');
    if (error?.response?.status === 401 && !isLogin) {
      usePortalAuthStore.getState().clear();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/portal/login')) {
        window.location.assign('/portal/login');
      }
    }
    return Promise.reject(error);
  },
);
